import type { components } from '@/api'
import type { User as BackendUser, ResourceRole } from '@/backend/types'

export type AssignableRole = Exclude<ResourceRole, 'owner'>

/**
 * A resource member is a user that has been added to a resource (e.g. a workspace or a folder),
 * with the role they have been assigned in the resource.
 */
export type ResourceMember = {
  id: BackendUser['id']
  email: BackendUser['email']
  firstName: BackendUser['first_name']
  lastName: BackendUser['last_name']
  fullName?: string | null
  role: ResourceRole
  lastActive?: BackendUser['last_activity_at']
  profilePictureUrl?: string | null
  /**
   * Resources that the user must be granted access to in order for this membership
   * to be effective.
   */
  missingAccess: {
    id: string
    resourceType: ResourceType
  }[]
}

export type ResourceAccessType =
  components['schemas']['Users.UpdateResourceAccessTypeRequest']['access_type']

/** All types of domain object that permissions can be scoped to. */
export type ResourceType = components['schemas']['Users.MissingAccessItem']['resource_type']
export const ResourceType: { [T in ResourceType]: T } = {
  hub: 'hub',
  workspace: 'workspace',
  project: 'project',
  folder: 'folder',
  case: 'case',
  integration: 'integration',
  space: 'space',
  user: 'user',
}

export type Permission =
  components['schemas']['Users.ResourceEffectiveMemberResponse']['permissions'][number]
export const Permission: { [T in Permission]: T } = {
  add_members: 'add_members',
  create_entities: 'create_entities',
  create_projects: 'create_projects',
  delete_entities: 'delete_entities',
  delete_projects: 'delete_projects',
  delete_workspaces: 'delete_workspaces',
  invite_members: 'invite_members',
  manage_billing: 'manage_billing',
  manage_hubs: 'manage_hubs',
  manage_project_exports: 'manage_project_exports',
  manage_properties: 'manage_properties',
  manage_tool_integrations: 'manage_tool_integrations',
  manage_triggers: 'manage_triggers',
  manage_views: 'manage_views',
  read_others: 'read_others',
  recalculate_entities: 'recalculate_entities',
  remove_members: 'remove_members',
  update_entities: 'update_entities',
  update_members: 'update_members',
  update_projects: 'update_projects',
  update_workspaces: 'update_workspaces',
  use_ask_go: 'use_ask_go',
  manage_integrations: 'manage_integrations',
  use_spaces: 'use_spaces',
  use_cases: 'use_cases',
  create_folders: 'create_folders',
  update_folders: 'update_folders',
  delete_folders: 'delete_folders',
  set_access_type: 'set_access_type',
  manage_service_accounts: 'manage_service_accounts',
}
